import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addAppealRespondbackFeedback,
  addHigherAdminRespondbackFeedback,
  editAppealRequest2,
  editDefendantAppeal2,
  editHigherAdminFeedbackRequest2,
} from "services/appealService";
import { Roles } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  type: "defendant" | "prosecutor";
  appealId?: number;
  higherAdminFeedbackId?: number;
  respondBackTo?: string;
  onClose: (refetch: boolean) => void;
};

const RespondBackComment: FC<Props> = ({
  type = "prosecutor",
  appealId,
  higherAdminFeedbackId,
  respondBackTo,
  onClose,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState<string>("");
  const [feedbackFile, FeedbackFile] = useState<File | null>(null);

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const validateForm = () => {
    // File is optional — only the comment is required.
    if (!comment) return t("comment_required");
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const data = {
      actor: isProsecutor
        ? "PROSECUTOR"
        : isRegistrar
          ? "REGISTRAR"
          : isCoordinator
            ? "COORDINATOR"
            : "OTHER",
      feedback_file: feedbackFile,
      status: "RESPONDED",
      feedback_text: comment,
      appeal_request: type === "prosecutor" ? appealId || undefined : undefined,
      defendant_appeal_request:
        type === "defendant" ? appealId || undefined : undefined,

      feedback_request: higherAdminFeedbackId || undefined,

      feedback_by: loginData?.full_name || "",
      feedback_by_id: loginData?.user_id || "",
    };

    const api = appealId
      ? addAppealRespondbackFeedback
      : addHigherAdminRespondbackFeedback;
    api(data)
      .then(() => {
        if (type === "defendant" && appealId) {
          editDefendantAppeal2({
            id: appealId,
            status: "RESPONSE_PREPARED_BY_PROSECUTOR",
            coordinator_status: "RESPOND_BACK",
          });
        } else if (type === "prosecutor" && appealId) {
          editAppealRequest2({
            id: appealId,
            status: "COORDINATOR_RESPONDED_BACK",
            coordinator_status: "RESPOND_BACK",
          });
        } else if (higherAdminFeedbackId) {
          editHigherAdminFeedbackRequest2({
            id: higherAdminFeedbackId,
            status: respondBackTo,
            coordinator_status: "RESPOND_BACK",
          });
        }
        showTooltip(t("successfully_updated"), "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative flex w-full max-w-2xl flex-col overflow-y-auto rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white px-8 py-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-1.5 rounded-full bg-amber-500" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t("respond_back")}
              </h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {t("respond_back_form_desc", {
                  defaultValue:
                    "Send the request back with your comment. Attaching a file is optional.",
                })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onClose(false)}
            aria-label="Close"
            className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                {t("respond_back_feedback")}
                <span className="text-red-500"> *</span>
              </label>
              <p className="text-xs text-gray-400 mb-2">
                {t("respond_back_comment_hint", {
                  defaultValue:
                    "Explain what needs to be corrected or addressed.",
                })}
              </p>
              <textarea
                rows={5}
                className="bg-white border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:ring focus:ring-blue-300"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>

            <div>
              <FileUpload
                label={`${t("feedback_file")} (${t("optional", {
                  defaultValue: "Optional",
                })})`}
                fileTypes="*"
                info={t("respond_back_file_hint", {
                  defaultValue: "Attach a supporting document if needed.",
                })}
                required={false}
                onChange={(file) => FeedbackFile(file)}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium"
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="submit"
                className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 transition-colors ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    {LoadingIcon}
                    {t("loading")}
                  </>
                ) : (
                  t("respond_back_button")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RespondBackComment;
